import React, { useEffect, useState } from "react";
import { Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nContext";
import { formatPrice } from "@/utils/price";
import api from "@/lib/api";

/**
 * Shows the currently active promo code prominently (never hardcoded - fetched from
 * /api/promo/active) so visitors actually discover and use it, instead of a blind "got a
 * promo code?" input nobody can guess. Reused on the wizard, checkout and public pricing page.
 */
export function usePromoActive() {
  const [promo, setPromo] = useState(null);
  useEffect(() => {
    api
      .get("/promo/active")
      .then(({ data }) => {
        if (data && data.code) setPromo(data);
      })
      .catch(() => {});
  }, []);
  return promo;
}

export default function PromoBanner({ onApply, applying, className = "" }) {
  const { language } = useI18n();
  const promo = usePromoActive();
  if (!promo) return null;

  const discountText = promo.percent_off ? `-${promo.percent_off}%` : `-${formatPrice(promo.amount_off, language)}`;

  return (
    <div
      className={`flex flex-wrap items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-primary/60 bg-primary/10 px-4 py-3 text-center shadow-[0_0_20px_-4px_hsl(var(--primary)/0.4)] ${className}`}
      data-testid="promo-banner"
    >
      <Gift className="h-4 w-4 shrink-0 text-primary" />
      <p className="text-sm font-semibold text-primary">
        {language === "en" ? (
          <>
            Use code <span className="font-mono tracking-wide">{promo.code}</span> for {discountText} off!
          </>
        ) : (
          <>
            Code <span className="font-mono tracking-wide">{promo.code}</span> : {discountText} sur votre commande !
          </>
        )}
      </p>
      {onApply && (
        <Button
          size="sm"
          variant="outline"
          className="rounded-full border-primary/60 text-primary"
          data-testid="promo-banner-apply-btn"
          disabled={applying}
          onClick={() => onApply(promo.code)}
        >
          {applying ? (language === "en" ? "Applying..." : "Application...") : language === "en" ? "Apply now" : "Appliquer"}
        </Button>
      )}
    </div>
  );
}
